<template>
    <div v-if="presentation.entries.length" class="search-status">
        <div class="live-status-label">Live search status:</div>
        <button
            ref="trigger"
            type="button"
            class="search-status-button"
            :class="`text-${presentation.summary.color}`"
            aria-label="More info about live search"
            title="Live-search details"
            aria-haspopup="dialog"
            :aria-describedby="previewVisible ? previewId : null"
            @click="openDetails"
            @mouseenter="showPreview"
            @mouseleave="scheduleHide"
            @focusin="showPreview"
            @focusout="scheduleHide"
            @keydown.esc.stop.prevent="hidePreview"
        >
            <span class="d-flex align-items-center gap-1">
                <span role="status" aria-live="polite">{{ presentation.summary.label }}</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.5" />
                    <path d="M10 9v5" stroke="currentColor" stroke-width="1.5" />
                    <circle cx="10" cy="6" r="1" fill="currentColor" />
                </svg>
            </span>
        </button>
        <Teleport to="body">
            <div
                v-if="previewVisible"
                :id="previewId"
                ref="preview"
                role="tooltip"
                class="live-search-preview"
                :style="previewPosition"
                @mouseenter="keepPreview"
                @mouseleave="scheduleHide"
            >
                <LiveSearchStatusContent
                    :runs="runs"
                    :loading="loading"
                    :resume-failed="resumeFailed"
                />
            </div>
            <dialog
                ref="details"
                class="search-status-dialog"
                :aria-labelledby="titleId"
                @click="closeOnBackdrop"
            >
                <div class="modal-header">
                    <h5 :id="titleId" class="modal-title">Live-search status</h5>
                    <button
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                        @click="$refs.details.close()"
                    />
                </div>
                <div class="modal-body">
                    <LiveSearchStatusContent
                        :runs="runs"
                        :loading="loading"
                        :resume-failed="resumeFailed"
                    />
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="$refs.details.close()">
                        Close
                    </button>
                </div>
            </dialog>
        </Teleport>
    </div>
</template>

<script>
    import LiveSearchStatusContent from '@/components/shared/LiveSearchStatusContent.vue'
    import { describeLiveSearch } from '@/services/liveSearchPresentation'

    export default {
        name: 'LiveSearchStatus',
        components: { LiveSearchStatusContent },
        props: {
            loading: { type: Boolean, default: false },
            resumeFailed: { type: Boolean, default: false },
            runs: { type: Array, default: () => [] },
        },
        data() {
            return { previewVisible: false, previewPosition: {}, hideTimer: null }
        },
        computed: {
            presentation() {
                return describeLiveSearch(this)
            },
            previewId() {
                return `live-search-preview-${this.$.uid}`
            },
            titleId() {
                return `live-search-title-${this.$.uid}`
            },
        },
        mounted() {
            window.addEventListener('resize', this.hidePreview)
            window.addEventListener('scroll', this.dismissPreviewOnScroll, true)
            window.addEventListener('keydown', this.dismissPreview)
        },
        beforeUnmount() {
            clearTimeout(this.hideTimer)
            window.removeEventListener('resize', this.hidePreview)
            window.removeEventListener('scroll', this.dismissPreviewOnScroll, true)
            window.removeEventListener('keydown', this.dismissPreview)
        },
        methods: {
            dismissPreviewOnScroll(event) {
                if (event.target !== this.$refs.preview) this.hidePreview()
            },
            dismissPreview(event) {
                if (event.key === 'Escape' && this.previewVisible) this.hidePreview()
            },
            async showPreview(event) {
                if (this.$refs.details?.open) return
                if (event?.type === 'mouseenter' && !window.matchMedia('(hover: hover)').matches)
                    return
                this.keepPreview()
                this.previewVisible = true
                await this.$nextTick()
                const trigger = this.$refs.trigger?.getBoundingClientRect()
                const panel = this.$refs.preview?.getBoundingClientRect()
                if (!trigger || !panel) return
                const left = Math.max(
                    12,
                    Math.min(trigger.left, window.innerWidth - panel.width - 12)
                )
                const below = trigger.bottom + 8
                const top =
                    below + panel.height <= window.innerHeight - 12
                        ? below
                        : Math.max(12, trigger.top - panel.height - 8)
                this.previewPosition = { left: `${left}px`, top: `${top}px` }
            },
            keepPreview() {
                clearTimeout(this.hideTimer)
            },
            scheduleHide() {
                this.keepPreview()
                this.hideTimer = setTimeout(this.hidePreview, 150)
            },
            hidePreview() {
                this.keepPreview()
                this.previewVisible = false
            },
            openDetails() {
                this.hidePreview()
                this.$refs.details.showModal()
            },
            closeOnBackdrop(event) {
                if (event.target === this.$refs.details) this.$refs.details.close()
            },
        },
    }
</script>

<style scoped>
    .search-status {
        margin-left: 16px;
        align-self: flex-start;
    }
    .live-status-label {
        font-weight: 600;
        color: var(--bs-body-color, #212529);
        margin-bottom: 4px;
    }
    .search-status-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 4px 8px;
        border: 1px solid currentColor;
        border-radius: 8px;
        font: inherit;
        text-align: left;
        color: inherit;
        background: transparent;
        cursor: pointer;
        transition: background-color 0.15s ease;
    }
    .search-status-button:hover,
    .search-status-button:active {
        background-color: var(--bs-tertiary-bg, #f5f5f5);
    }
    .search-status-button svg {
        flex-shrink: 0;
    }
    .search-status-button:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
    .live-search-preview {
        position: fixed;
        width: min(400px, calc(100vw - 24px));
        max-height: calc(100vh - 24px);
        overflow: auto;
        padding: 16px;
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 10px;
        box-shadow: 0 6px 24px rgb(0 0 0 / 15%);
        z-index: 1050;
        color: var(--bs-body-color, #212529);
        background: var(--bs-body-bg, #fff);
    }
    .search-status-dialog {
        position: fixed;
        inset: 0;
        margin: auto;
        overflow: auto;
        width: min(480px, calc(100vw - 32px));
        max-height: 85vh;
        padding: 0;
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 12px;
        color: var(--bs-body-color, #212529);
        background: var(--bs-body-bg, #fff);
    }
    .search-status-dialog::backdrop {
        background: rgb(0 0 0 / 45%);
    }
    .modal-header,
    .modal-body,
    .modal-footer {
        padding: 16px 24px;
    }
    .modal-header {
        gap: 12px;
    }
    .modal-header .btn-close {
        margin-left: auto;
        flex-shrink: 0;
    }
    .text-warning {
        color: var(--bs-warning-text-emphasis, #664d03) !important;
    }
</style>
